import React, { Fragment, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { cloneDeep, isEmpty } from 'lodash';
import { IProposalDetailsSummary } from '../../index2';
import { IInsuredData, IProposalPageFormState } from '../../form';
import { fetchedPersonAddressList } from '../../queries';
import { extractPersonsAddressesFixed } from '../../utils';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import ProposalPolicyPersonDrawer from '../../../../proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import { removeObjectAtIndex } from '@utils/helper-utils';
import { Tabs, Tab, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import TabPanel from '@components/layout/tabs/TabPanel';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import { fetchedPersonsWithPagination } from '../../../../proposal-details-page/queries';
import { IAbstractRecord } from '@models';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import { extractPersonsFromQuery } from '../../../../proposal-details-page/utils';
import { MAIN_THEME } from '@styles';
import { useResetKey } from '@hooks/useResetKey';

interface IPolicyDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
  lovs: Record<string, Record<string, string>>;
  maxNumber: number;
  data: IProposalDetailsSummary;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '33px 23px 23px',
    marginBottom: '14px',
    position: 'relative',
    borderTopLeftRadius: 'unset',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(4, 23.5%)`,
    gap: '8px',
    justifyContent: 'flex-start',
    alignItems: 'baseline',
  },
  addBtn: {
    marginLeft: '2px',
    minWidth: 'auto',
    minHeight: 'auto',
    backgroundColor: 'transparent',
    padding: 0,
    margin: '0 0 0 10px',
    alignSelf: 'center',
    border: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: '8px',
    right: '0',
    margin: '0',
  },
  clearBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0 !important',
  },
  label: {
    color: MAIN_THEME.color.black,
    margin: '0px!important',
    padding: '0px!important',
    marginBottom: '4px!important',
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
  labelHeader: {
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
    marginBottom: '4px!important',
  },
  cardTitle: {
    fontSize:`${MAIN_THEME.fontSize.rg} !important`,
    lineHeight: '21px !important',
    margin: '0 0 16px',
    padding: '0',
    fontFamily: `${MAIN_THEME.font.bold} !important`,
  },
  tabLabel: {
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.regular,
    textTransform: 'none',
    lineHeight: '18px',
    color: MAIN_THEME.color.black,
    display: 'flex',
    alignItems: 'center',
  },
  tabActiveTitle: {
    fontFamily: `${MAIN_THEME.font.medium} !important`,
    fontWeight: 600,
  },
  tabIconButton: {
    marginLeft: '8px',
    padding: '0',
  },
  tabsContainer: {
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    padding: '0',
    '&::-webkit-scrollbar': {
      height: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: MAIN_THEME.color.white86,
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: MAIN_THEME.color.white65,
    },
  },
  tabsWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  indicator: {
    display: 'none',
  },
  fieldRowChildSpan2: {
    gridColumn: 'span 2',
  },
  tabsRoot: {
    minHeight: 'auto',
    overflowX: 'auto',
    '& .MuiTabs-flexContainer': {
      borderBottom: 'none',
    },
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 72,
    minHeight: 'auto',
    padding: '6px 12px',
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderBottom: 'none',
    borderRadius: '4px 4px 0 0',
    marginRight: '2px',
    backgroundColor: MAIN_THEME.color.white92,
    '&.Mui-selected': {
      fontWeight: 600,
    },
  },
}));

const ProposalMedicalPrincipalWidget: React.FC<IPolicyDetailsWidgetProps> = ({
  pageState,
  onPageStateUpdate,
  disabledForm,
  maxNumber,
  lovs,
  data,
}) => {
  const { classes } = useStyles();
  const { id: entityId } = useParams<{ id: string }>();
  const [tabsKey, refreshTabs] = useResetKey('tabsKey');

  const [loaded, setLoaded] = useState<boolean>(false);

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] = useState<number>(-1);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] = useState<number>(-1);
  const [currentSection, setCurrentSection] = useState<string | null>(null);

  const [lists, setLists] = useState<{
    id: Record<number, Record<string, string>>;
    insuredAddresses: Record<number, Record<string, string>>;
  }>({
    id: {},
    insuredAddresses: {},
  });

  const insuredValues = pageState.values.policyPersons.insured;
  const insuredErrors = pageState.errors.policyPersons.insured;
  const insuredTouched = pageState.touched.policyPersons.insured;

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const [fetchedPersonsLazy, { loading: personLoading }] = useLazyQuery(
    fetchedPersonsWithPagination()
  );
  const [getPersonAddressListLazy] = useLazyQuery(fetchedPersonAddressList());

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleOpenDrawer = (section: string, rowIndex: number) => {
    setCurrentSection(section);
    setNewPersonDrawerOpen(rowIndex);
  };

  const handleOpenAddressDrawer = (section: string, rowIndex: number) => {
    setCurrentSection(section);
    setNewAddressDrawerOpen(rowIndex);
  };

  const handleInsuredAddresses = async (personId: string) => {
    const result = await getPersonAddressListLazy({
      variables: { selectedPersonID: personId },
      errorPolicy: 'all',
    });
    return extractPersonsAddressesFixed(result.data);
  };

  const initialize = () => {
    const newList: Record<number, Record<string, string>> = {};
    insuredValues.forEach((insured, index) => {
      if (!newList[index]) {
        newList[index] = {};
      }
      newList[index][insured.address] = insured.addressDetails;
    });
    setLists((prevLists) => ({
      ...prevLists,
      insuredAddresses: newList,
    }));
    setLoaded(true);
  };

  useEffect(() => {
    initialize();
  }, []);

  const onFieldBlur = async (fieldName: string, rowIndex: number) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.policyPersons.insured[rowIndex][fieldName] = true;

    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = async (
    fieldName: string,
    value: unknown,
    rowIndex: number,
    touched = false,
    updatedList: typeof lists = lists
  ) => {
    const newPageState = cloneDeep(pageState);

    if (fieldName === 'id') {
      newPageState.values.policyPersons.insured[rowIndex][fieldName] =
        value as string;
      newPageState.errors.policyPersons.insured[rowIndex][fieldName] = '';
      if (touched) {
        newPageState.touched.policyPersons.insured[rowIndex][fieldName] = true;
      }

      newPageState.values.policyPersons.insured[rowIndex].address = '';
      newPageState.values.policyPersons.insured[rowIndex].addressDetails = '';

      if (!isEmpty(value)) {
        const searchAddressResult = await handleInsuredAddresses(
          value as string
        );

        const newLists = { ...updatedList };
        const insuredAddresses = searchAddressResult;
        const extractedAddresses: IAbstractRecord = {};
        if (insuredAddresses) {
          Object.keys(insuredAddresses).forEach((key) => {
            extractedAddresses[key] = insuredAddresses[key].address;
            if (insuredAddresses[key].isPrimary) {
              newPageState.values.policyPersons.insured[rowIndex].address = key;
              newPageState.values.policyPersons.insured[
                rowIndex
              ].addressDetails = insuredAddresses[key].address;
            }
          });
          newPageState.errors.policyPersons.insured[rowIndex].insuredAddress =
            '';
          newLists['insuredAddresses'][rowIndex] = extractedAddresses;
          setLists(newLists);
        }
      }
    } else {
      (newPageState.values.policyPersons.insured[rowIndex] as any)[fieldName] =
        value;

      newPageState.errors.policyPersons.insured[rowIndex][fieldName] = '';
      if (touched) {
        newPageState.touched.policyPersons.insured[rowIndex][fieldName] = true;
      }
    }

    onPageStateUpdate(newPageState);
  };

  const onPersonAddition = async (
    personId: string,
    personFullName?: string
  ) => {
    const rowIndex = newPersonDrawerOpen;

    const newPageState = cloneDeep(pageState);

    const newLists = cloneDeep(lists);

    newPageState.values.policyPersons.insured[rowIndex]['id'] = personId;
    newPageState.errors.policyPersons.insured[rowIndex]['id'] = '';
    newPageState.touched.policyPersons.insured[rowIndex]['id'] = true;

    newLists.id[rowIndex] = {};
    newLists.id[rowIndex][personId] = personFullName;

    const searchAddressResult = await handleInsuredAddresses(personId);

    const insuredAddresses = searchAddressResult;
    const extractedAddresses: IAbstractRecord = {};
    if (insuredAddresses) {
      Object.keys(insuredAddresses).forEach((key) => {
        extractedAddresses[key] = insuredAddresses[key].address;
        if (insuredAddresses[key].isPrimary) {
          newPageState.values.policyPersons.insured[rowIndex].address = key;
          newPageState.values.policyPersons.insured[rowIndex].addressDetails =
            insuredAddresses[key].address;
        }
      });
      newPageState.errors.policyPersons.insured[rowIndex].insuredAddress = '';
      newLists['insuredAddresses'][rowIndex] = extractedAddresses;
    }

    if (Object.keys(newLists['insuredAddresses'][rowIndex]).length === 1) {
      newPageState.values.policyPersons.insured[rowIndex]['address'] =
        Object.keys(newLists['insuredAddresses'][rowIndex])[0];
    }

    setLists(newLists);
    onPageStateUpdate(newPageState);
    refreshTabs();
  };

  const onAddressAddition = async (
    address: string,
    addressFullName: string
  ) => {
    const rowIndex = newAddressDrawerOpen;

    const newPageState = cloneDeep(pageState);

    const newLists = cloneDeep(lists);

    newPageState.values.policyPersons.insured[rowIndex]['address'] = address;
    newPageState.errors.policyPersons.insured[rowIndex]['address'] = '';
    newPageState.touched.policyPersons.insured[rowIndex]['address'] = true;

    newLists.insuredAddresses[rowIndex] = {};
    newLists.insuredAddresses[rowIndex][address] = addressFullName;

    newPageState.values.policyPersons.insured[rowIndex].address = address;
    newPageState.values.policyPersons.insured[rowIndex].addressDetails =
      addressFullName;

    setLists(newLists);
    onPageStateUpdate(newPageState);
  };

  const getInsuredLov = (row: IInsuredData) => {
    const result = { ...lovs.insuredRelations };
    if (row.relation !== 'SPOUSE') {
      if (insuredValues.some((a) => a.relation === 'SPOUSE')) {
        delete result['SPOUSE'];
      }
    }
    return result;
  };

  const renderInsuredRow = (row: IInsuredData) => {
    const rowIndex = insuredValues.indexOf(row);
    return (
      <div className={classes.container} key={rowIndex}>
        <div className={classes.fieldRow}>
          <div className={classes.fieldRowChildSpan2}>
            <WithLinkFormField
              linkText={disabledForm ? '' : 'Add Person'}
              onLinkClick={() => {
                handleOpenDrawer('insured', rowIndex);
              }}
            >
              <AutocompleteFormField
                key="id"
                name="id"
                title="Insured*"
                placeholder="Insured"
                value={insuredValues[rowIndex].id}
                error={
                  insuredTouched[rowIndex]?.id ? insuredErrors[rowIndex].id : ''
                }
                disabled={disabledForm || personLoading}
                material
                onChange={(v) => {
                  onFieldUpdate('id', v, rowIndex);
                }}
                loader={personLoading}
                fetchOptions={function (
                  value,
                  pagination,
                  hasDefaultValue
                ): Promise<{
                  options: Record<string, string>;
                  totalCount: number;
                }> {
                  const defaultValue = hasDefaultValue ? value : null;
                  return fetchedPersonsLazy({
                    variables: {
                      searchKeyword: hasDefaultValue ? null : value,
                      pagination,
                      selectedPersonID: defaultValue,
                    },
                  }).then((result) => {
                    const options = extractPersonsFromQuery(
                      result.data?.Production?.queries
                        ?.GetAssignedPersonsWithPagination?.items
                    );
                    return {
                      options,
                      totalCount:
                        result.data?.Production?.queries
                          ?.GetAssignedPersonsWithPagination?.paging
                          ?.totalCount,
                    };
                  });
                }}
              />
            </WithLinkFormField>
          </div>
          <div className={classes.fieldRowChildSpan2}>
            {row.isPrincipal ? (
              <WithLinkFormField
                linkText={disabledForm ? '' : 'Add Address'}
                onLinkClick={() => {
                  handleOpenAddressDrawer('insured', rowIndex);
                }}
              >
                <SelectFormField
                  key="address"
                  name="address"
                  title="Insured Address"
                  placeholder="Insured Address"
                  value={insuredValues[rowIndex].address}
                  disabled={disabledForm}
                  material
                  error={
                    insuredTouched[rowIndex]?.address
                      ? insuredErrors[rowIndex].address
                      : ''
                  }
                  onChange={(v) => onFieldUpdate('address', v, rowIndex)}
                  selectOptions={lists.insuredAddresses[rowIndex]}
                />
              </WithLinkFormField>
            ) : (
              <SelectFormField
                key="relation"
                name="relation"
                title="Relation*"
                placeholder="Relation"
                value={insuredValues[rowIndex].relation}
                error={
                  insuredTouched[rowIndex].relation
                    ? insuredErrors[rowIndex].relation
                    : ''
                }
                onChange={(v) => onFieldUpdate('relation', v, rowIndex)}
                disabled={disabledForm}
                selectOptions={getInsuredLov(row)}
                material
              />
            )}
          </div>
          <TextInputFormField
            key="cardNumber"
            name="cardNumber"
            title="Card Number"
            placeholder="Card Number"
            value={insuredValues[rowIndex].cardNumber}
            error={
              insuredTouched[rowIndex].cardNumber
                ? insuredErrors[rowIndex].cardNumber
                : ''
            }
            onBlur={() => onFieldBlur('cardNumber', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('cardNumber', v.target.value, rowIndex)
            }
            disabled={disabledForm}
            material
          />
          <SelectFormField
            key="continuity"
            name="continuity"
            title="Continuity"
            placeholder="Continuity"
            value={insuredValues[rowIndex].continuity}
            error={
              insuredTouched[rowIndex].continuity
                ? insuredErrors[rowIndex].continuity
                : ''
            }
            disabled={disabledForm}
            selectOptions={lovs.continuity}
            onBlur={() => onFieldBlur('continuity', rowIndex)}
            onChange={(v) => onFieldUpdate('continuity', v, rowIndex)}
            material
          />

          <TextInputFormField
            key="tpaReference1"
            name="tpaReference1"
            title="TPA Reference 1"
            placeholder="TPA Reference 1"
            value={insuredValues[rowIndex].tpaReference1}
            error={
              insuredTouched[rowIndex].tpaReference1
                ? insuredErrors[rowIndex].tpaReference1
                : ''
            }
            onBlur={() => onFieldBlur('tpaReference1', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('tpaReference1', v.target.value, rowIndex)
            }
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="tpaReference2"
            name="tpaReference2"
            title="TPA Reference 2"
            placeholder="TPA Reference 2"
            value={insuredValues[rowIndex].tpaReference2}
            error={
              insuredTouched[rowIndex].tpaReference2
                ? insuredErrors[rowIndex].tpaReference2
                : ''
            }
            onBlur={() => onFieldBlur('tpaReference2', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('tpaReference2', v.target.value, rowIndex)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            key="exclusions"
            name="exclusions"
            title="Exclusions"
            placeholder="Exclusions"
            value={insuredValues[rowIndex].exclusions}
            error={
              insuredTouched[rowIndex].exclusions
                ? insuredErrors[rowIndex].exclusions
                : ''
            }
            onBlur={() => onFieldBlur('exclusions', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('exclusions', v.target.value, rowIndex)
            }
            disabled={disabledForm}
            material
            fullWidth
          />

          <TextInputFormField
            key="remarks"
            name="remarks"
            title="Remarks"
            placeholder="Remarks"
            value={insuredValues[rowIndex].remarks}
            error={
              insuredTouched[rowIndex].remarks
                ? insuredErrors[rowIndex].remarks
                : ''
            }
            onBlur={() => onFieldBlur('remarks', rowIndex)}
            onChange={(v) => onFieldUpdate('remarks', v.target.value, rowIndex)}
            disabled={disabledForm}
            material
            fullWidth
          />
        </div>
      </div>
    );
  };

  const onInsuredRowAdd = () => {
    if (!disabledForm && insuredValues.length < maxNumber) {
      const newPageState = cloneDeep(pageState);
      const newRowIndex = newPageState.values.policyPersons.insured.length;
      newPageState.values.policyPersons.insured.push({
        id: '',
        order: `${newRowIndex + 1}`,
        address: '',
        grLimitDays: undefined,
        cardNumber: '',
        continuity: '',
        tpaReference1: '',
        tpaReference2: '',
        exclusions: '',
        remarks: '',
        relation: '',
        isPrincipal: false,
      });

      newPageState.errors.policyPersons.insured.push({
        id: '',
        address: '',
        grLimitDays: '',
        cardNumber: '',
        continuity: '',
        tpaReference1: '',
        tpaReference2: '',
        exclusions: '',
        remarks: '',
        type: '',
        relation: '',
      });

      newPageState.touched.policyPersons.insured.push({
        id: false,
        address: false,
        grLimitDays: false,
        cardNumber: false,
        continuity: false,
        tpaReference1: false,
        tpaReference2: false,
        exclusions: false,
        remarks: false,
        type: false,
        relation: false,
      });

      onPageStateUpdate(newPageState);

      setSelectedTab(insuredValues.length); // Set to the new index
    }
  };

  const onInsuredRowDelete = (rowIndex: number) => {
    if (!disabledForm) {
      const newPageState = cloneDeep(pageState);
      newPageState.values.policyPersons.insured = removeObjectAtIndex(
        newPageState.values.policyPersons.insured,
        rowIndex
      );
      newPageState.errors.policyPersons.insured = removeObjectAtIndex(
        newPageState.errors.policyPersons.insured,
        rowIndex
      );
      newPageState.touched.policyPersons.insured = removeObjectAtIndex(
        newPageState.touched.policyPersons.insured,
        rowIndex
      );

      setLists((prevLists) => {
        const newId: Record<number, Record<string, string>> = {};
        const newInsuredAddresses: Record<number, Record<string, string>> = {};

        Object.keys(prevLists.id).forEach((key) => {
          const keyNum = Number(key);
          if (keyNum < rowIndex) {
            newId[keyNum] = prevLists.id[keyNum];
            newInsuredAddresses[keyNum] = prevLists.insuredAddresses[keyNum];
          } else if (keyNum > rowIndex) {
            newId[keyNum - 1] = prevLists.id[keyNum];
            newInsuredAddresses[keyNum - 1] =
              prevLists.insuredAddresses[keyNum];
          }
        });

        return {
          id: newId,
          insuredAddresses: newInsuredAddresses,
        };
      });

      onPageStateUpdate(newPageState);

      // Adjust selectedTab
      if (selectedTab >= newPageState.values.policyPersons.insured.length) {
        setSelectedTab(
          Math.max(0, newPageState.values.policyPersons.insured.length - 1)
        );
      }
    }
  };

  if (!loaded) {
    return null;
  }

  return (
    <WidgetPaper>
      <WidgetSection title="Insured Details">
        <div className={classes.tabsContainer}>
          <div className={classes.tabsWrapper}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="Insured Tabs"
              variant="scrollable"
              scrollButtons="auto"
              classes={{
                root: classes.tabsRoot,
                indicator: classes.indicator,
              }}
            >
              {insuredValues.map((v, index) => (
                <Tab
                  key={index}
                  className={classes.tabRoot}
                  label={
                    <div
                      className={`${classes.tabLabel} ${
                        index === selectedTab ? classes.tabActiveTitle : ''
                      }`}
                    >
                      {v.isPrincipal ? 'Principal' : `Adherent ${index}`}
                      {!disabledForm &&
                        insuredValues.length > 1 &&
                        !v.isPrincipal && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onInsuredRowDelete(index);
                            }}
                            className={classes.tabIconButton}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                    </div>
                  }
                  id={`insured-tab-${index}`}
                  aria-controls={`insured-tabpanel-${index}`}
                  value={index}
                />
              ))}
            </Tabs>
          </div>
          {insuredValues.length < maxNumber && (
            <IconButton
              className={classes.addBtn}
              onClick={onInsuredRowAdd}
              disabled={disabledForm}
              aria-label="Add"
            >
              <AddIcon color="primary" />
            </IconButton>
          )}
        </div>
        <Fragment key={tabsKey}>
          {insuredValues.map(
            (v, index) =>
              selectedTab === index && (
                <TabPanel index={index} key={index}>
                  {renderInsuredRow(v)}
                </TabPanel>
              )
          )}
        </Fragment>
        {newPersonDrawerOpen !== -1 && currentSection === 'insured' && (
          <ProposalPolicyPersonDrawer
            open={newPersonDrawerOpen !== -1}
            onClose={() => setNewPersonDrawerOpen(-1)}
            section={currentSection}
            planId={''}
            onSuccess={() => undefined}
            proposalId={entityId}
            lineExternalCode={data?.LineId?.ExternalCode}
            onSubmitSuccess={onPersonAddition}
          />
        )}
        {newAddressDrawerOpen !== -1 && currentSection === 'insured' && (
          <PersonAddressDrawer
            open={newAddressDrawerOpen !== -1}
            onClose={() => setNewAddressDrawerOpen(-1)}
            personId={
              pageState.values.policyPersons.insured[newAddressDrawerOpen].id
            }
            onSuccess={() => undefined}
            onSubmitSuccess={onAddressAddition}
          />
        )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export default ProposalMedicalPrincipalWidget;
